import { Link, useNavigate } from "react-router-dom";
import { CircleHelp, LifeBuoy, LogOut, Menu, Settings, User as UserIcon, Briefcase, ChevronDown } from "lucide-react";
import { useAuthStore } from "@/app/store/auth.store";
import { useUIStore } from "@/app/store/ui.store";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dropdown, DropdownItem, DropdownSeparator } from "@/components/ui/dropdown";
import { ThemeToggle } from "./ThemeToggle";
import { LocationSwitcher } from "./LocationSwitcher";
import { ModuleSearch } from "./ModuleSearch";
import { useMyPhotoUrl } from "@/features/employees/hooks/useMyPhoto";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import api from "@/lib/api/client";
import { queryClient } from "@/lib/api/queryClient";

export function Topbar() {
  const { user, clear } = useAuthStore();
  const setMobileNav = useUIStore((s) => s.setMobileNav);
  const navigate = useNavigate();

  const signOut = async () => {
    // Best-effort: if the network is down the local session still ends. A
    // logout button that fails because the server is unreachable is worse than
    // one that clears optimistically.
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    queryClient.clear();
    clear();
    navigate("/login", { replace: true });
  };

  const name = user ? `${user.firstName} ${user.lastName}` : "";
  const photoUrl = useMyPhotoUrl();

  return (
    <header className="workspace-topbar sticky top-0 z-30 flex h-14 shrink-0 items-center gap-1.5 border-b border-border bg-surface/80 px-2.5 backdrop-blur-md dark:border-white/[0.07] dark:bg-[#111626]/90 dark:shadow-[0_8px_26px_-20px_rgba(0,0,0,0.8)] sm:gap-2.5 sm:px-4">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={() => setMobileNav(true)}
        aria-label="Open navigation"
      >
        <Menu className="h-4 w-4" />
      </Button>

      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="dashboard-topbar-title hidden truncate">Overview</span>
        <ModuleSearch />
      </div>

      <div className="topbar-utility-links hidden items-center gap-1.5 2xl:flex">
        <Link
          to="/settings/profile"
          className="inline-flex h-9 items-center gap-2 rounded-lg px-2.5 text-[14px] font-semibold text-muted-foreground transition-colors hover:bg-violet-50 hover:text-violet-700 dark:hover:bg-violet-500/10 dark:hover:text-violet-200"
        >
          <Settings className="h-4 w-4 text-violet-500" />
          Settings
        </Link>
        {/* Support / Need help? links hidden for now, by request -- kept
            commented out so they can be brought back as-is.
        <a
          href="mailto:support@work4hr.ai"
          className="inline-flex h-9 items-center gap-2 rounded-lg px-2.5 text-[14px] font-semibold text-muted-foreground transition-colors hover:bg-cyan-50 hover:text-cyan-700 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
        >
          <LifeBuoy className="h-4 w-4 text-cyan-500" />
          Support
        </a>
        <a
          href="mailto:support@work4hr.ai?subject=Work4HR%20Help"
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 px-2.5 text-[14px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-500/10 transition-all hover:from-violet-500/15 hover:to-fuchsia-500/15 dark:text-violet-200"
        >
          <CircleHelp className="h-4 w-4 text-fuchsia-500" />
          Need help?
        </a>
        */}
      </div>

      <LocationSwitcher />

      <NotificationBell />

      <ThemeToggle />

      <Dropdown
        trigger={
          <button type="button" aria-label="Open profile menu" className="topbar-profile flex min-w-0 items-center gap-2 rounded-xl px-1.5 py-1 transition-colors hover:bg-accent">
            <Avatar name={name} src={photoUrl} size="sm" className="shrink-0" />
            {/* min-w-0 + truncate -- a long real name (this isn't a fixed
                short label) must never be what pushes the rest of the
                topbar off-screen at a narrower tablet width. */}
            <div className="hidden min-w-0 text-left sm:block">
              {/* Name only -- no role or email in the header, by request. */}
              <p className="max-w-[9rem] truncate text-[14px] font-semibold leading-tight lg:max-w-[12rem]">{name}</p>
            </div>
            <ChevronDown className="dashboard-profile-chevron hidden" aria-hidden="true" />
          </button>
        }
      >
        <Link to="/settings/profile">
          <DropdownItem icon={UserIcon}>Your profile</DropdownItem>
        </Link>
        {/* Distinct from "Your profile" above -- that's the lightweight
            settings page (avatar, theme, notification preferences, "how
            you appear to colleagues"), not the full HR record. This
            goes straight to the same rich Employment/Personal/History/
            Leave/Team/Documents/Assets view HR sees when looking at
            this same person, just scoped to the signed-in employee's
            own record -- no need to go find yourself in the Employees
            directory first. Hidden entirely for an account with no
            linked employee record at all (a Super Admin who was never
            actually employed, for instance) -- there is nothing this
            link could meaningfully point to for them. */}
        {/* Also hidden for the shared admin@ / hr@ logins (User.isSystemAccount)
            -- they have an Employee record behind them, but it isn't a real
            person's employment to look at. */}
        {user?.employeeId && !user?.isSystemAccount && (
          <Link to={`/employees/${user.employeeId}`}>
            <DropdownItem icon={Briefcase}>My details</DropdownItem>
          </Link>
        )}
        <Link to="/settings/security">
          <DropdownItem icon={Settings}>Security</DropdownItem>
        </Link>
        <DropdownSeparator />
        <DropdownItem icon={LogOut} destructive onClick={signOut}>
          Sign out
        </DropdownItem>
      </Dropdown>
    </header>
  );
}
